import type { LocaleCode } from '@ifixit/i18n/types';
import { Sheet, SheetContent, SheetPresence } from '@ifixit/ui/components/sheet';
import { useState, Suspense, lazy } from 'react';
import { DevicePickerModal } from '@ifixit/device-picker';

interface WorkbenchDrawerProps {
   storeLocale: LocaleCode;
   langid: string;
   indexPrefix: string;
   searchClient: Parameters<typeof DevicePickerModal>[0]['algoliaConfig']['searchClient'] | null;
   isOpen: boolean;
   onClose: () => void;
}

const LazyWorkbenchDrawerContent = lazy(() => import('./workbench-drawer-content'));
const LazyDevicePicker = lazy(() => import('./workbench-device-picker'));

export function WorkbenchDrawer({
   isOpen,
   onClose,
   storeLocale,
   langid,
   indexPrefix,
   searchClient,
}: WorkbenchDrawerProps) {
   const [isDevicePickerOpen, setDevicePickerOpen] = useState(false);

   if (!searchClient) {
      return null;
   }

   return (
      <>
         <Sheet
            open={isOpen && !isDevicePickerOpen}
            onOpenChange={open => {
               if (!open) {
                  onClose();
               }
            }}
         >
            {/* Match the Chakra DrawerContent this replaced, which had an
                explicit maxWidth="512px" (max-w-lg) at every viewport. The
                bare max-w-lg covers viewports below sm: a full-width drawer
                leaves no off-drawer area to click to dismiss, so
                WDWorkbenchTest::testVariantsRemoveGeneric times out.
                sm:max-w-lg is also required: SheetContent's side="right"
                variant ships sm:max-w-sm, which tailwind-merge keeps
                alongside the bare class and which wins the cascade at ≥sm.
                p-0 also swallows the variant's safe-area padding, so the
                pr re-adds it after the zeroing. */}
            <SheetContent
               side="right"
               className="w-full max-w-lg gap-0 p-0 pr-safe-right sm:max-w-lg"
            >
               <SheetPresence>
                  <Suspense>
                     <LazyWorkbenchDrawerContent
                        setDevicePickerOpen={setDevicePickerOpen}
                        langid={langid}
                     />
                  </Suspense>
               </SheetPresence>
            </SheetContent>
         </Sheet>

         {isOpen && (
            <LazyDevicePicker
               isOpen={isDevicePickerOpen}
               setOpen={setDevicePickerOpen}
               storeLocale={storeLocale}
               langid={langid}
               indexPrefix={indexPrefix}
               searchClient={searchClient}
            />
         )}
      </>
   );
}
